import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  type NotificationRow,
} from "@/hooks/useNotifications";
import { NotificationItem } from "./NotificationItem";

interface NotificationPanelProps {
  onClose?: () => void;
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | "unread">("unread");
  const { data: notifications = [], isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const visible = useMemo(() => {
    if (tab === "unread") return notifications.filter((n) => !n.read_at);
    return notifications;
  }, [notifications, tab]);

  const hasUnread = notifications.some((n) => !n.read_at);

  const handleClick = (n: NotificationRow) => {
    if (!n.read_at) markRead.mutate(n.id);
    if (n.entity_type === "order" && n.entity_id) {
      navigate(`/orders/${n.entity_id}`);
      onClose?.();
    } else if (n.entity_type === "trailer_migration") {
      navigate(`/trailers`);
      onClose?.();
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <Tabs value={tab} onValueChange={(v) => setTab(v as "all" | "unread")} className="flex-1 flex flex-col min-h-0">
        <div className="px-4 border-b flex items-center justify-between pt-[8px]">
          <TabsList className="bg-transparent p-0 h-auto gap-4">
            <TabsTrigger
              value="unread"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-2"
            >
              Unread
            </TabsTrigger>
            <TabsTrigger
              value="all"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary px-0 pb-2"
            >
              All
            </TabsTrigger>
          </TabsList>
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-blue-500"
            disabled={!hasUnread || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all as read
          </Button>
        </div>
        <TabsContent value={tab} className="flex-1 min-h-0 mt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : visible.length === 0 ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              {tab === "unread" ? "No unread notifications" : "No notifications yet"}
            </div>
          ) : (
            <ScrollArea className="h-full">
              <div className="space-y-2 p-3">
                {visible.map((n) => (
                  <NotificationItem key={n.id} notification={n} onClick={() => handleClick(n)} />
                ))}
              </div>
            </ScrollArea>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}